import { FC, useState } from 'react';

import { getBankAccountDisplayName } from 'common/bankAccount/utils';
import { useCompanyBankAccounts } from 'common/hooks/useCompanyBankAccounts';
import useCompanyContext from 'common/hooks/useCompanyContext';
import Heading from 'components/_core/Heading';
import SunshineCard from 'components/_core/SunshineCard';
import Text from 'components/_core/Text';
import BackButton from 'components/BackButton';
import ErrorView from 'components/ErrorView';
import Loader from 'components/Loader';
import { StepProps } from 'components/Stepper/types';
import useToast from 'components/ToastProvider/useToast';
import * as StepperLayout from 'layouts/StepperLayout';

import * as CommonStyles from '../../common/styles';
import NextButton from '../../components/NextButton';
import { useMandateCreationFlowContext } from '../../hooks/useMandateCreationFlowContext';
import SidebarConfirmationPasscode from './components/SidebarConfirmationPasscode';
import { useUploadB2BDirectDebitMandate } from './hooks/useUploadB2BDirectDebitMandate';
import locales from './locales';

const StepMandateRecap: FC<StepProps> = ({ goBack, goNext }) => {
  const { bankAccounts, error, loading, mainBankAccount } =
    useCompanyBankAccounts();

  const { setSharedState, sharedState } = useMandateCreationFlowContext();
  const { mandate } = sharedState;

  const { companyUserId } = useCompanyContext();

  const [hasApprovedAgreement, setHasApprovedAgreement] = useState(false);

  const [showPasscodeSidebar, setShowPasscodeSidebar] = useState(false);
  const { showToast } = useToast();

  const [uploadMandate, { loading: uploadMandateLoading }] =
    useUploadB2BDirectDebitMandate();

  if (loading) {
    return <Loader />;
  }

  if (error || !bankAccounts || !mainBankAccount) {
    return <ErrorView />;
  }

  const onSubmit = () => {
    if (!hasApprovedAgreement) {
      return;
    }
    setShowPasscodeSidebar(true);
  };

  const fromBankAccountId =
    mandate.bankAccountId || mainBankAccount?.bankAccountId;

  const onConfirmationSuccess = async (shortLiveToken: string) => {
    try {
      const { data } = await uploadMandate({
        context: {
          headers: {
            'short-lived-token': shortLiveToken,
          },
        },
        variables: {
          input: {
            bankAccountId: fromBankAccountId,
            bucketName: mandate.file.bucketName,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            companyUserId: companyUserId!,
            creditorIdentifier: mandate.creditorIdentifier,
            creditorName: mandate.creditorName,
            fileRef: mandate.file.fileRef,
            label: mandate.label,
            publicUrl: mandate.file.publicUrl,
            uniqueMandateReference: mandate.uniqueMandateReference,
          },
        },
      });

      if (data) {
        const {
          uploadB2BDirectDebitMandate: {
            directDebitMandate: { directDebitMandateId },
          },
        } = data;
        setSharedState({
          ...sharedState,
          createdMandateId: directDebitMandateId,
        });
      }
      goNext();
    } catch (_err) {
      showToast({
        message: locales.errorToast.message,
        title: locales.errorToast.title,
        type: 'error',
      });
    }
  };

  const canClickOnNext = hasApprovedAgreement;
  const hasMultipleBankAccounts = bankAccounts.length > 1;
  const mandateBankAccount = bankAccounts.find(
    (bankAccount) => bankAccount.bankAccountId === mandate.bankAccountId,
  );
  const bankAccountName = mandateBankAccount
    ? getBankAccountDisplayName(mandateBankAccount)
    : '';

  return (
    <StepperLayout.Root>
      <StepperLayout.Body>
        <CommonStyles.FormWrapper onValidSubmit={onSubmit}>
          <CommonStyles.HeaderContainer>
            <BackButton isDisabled={uploadMandateLoading} onClick={goBack} />
          </CommonStyles.HeaderContainer>

          <StepperLayout.BodyScrollableSection>
            <Heading marginBottom="space-40" size="lg">
              {locales.title}
            </Heading>

            <SunshineCard.Group marginBottom="space-40">
              <SunshineCard>
                <SunshineCard.Content
                  flexDirection="row"
                  justifyContent="space-between"
                >
                  <Text>{locales.recapItems.creditorName}</Text>
                  <Text fontWeight="weight-500" variant="primary">
                    {mandate.creditorName}
                  </Text>
                </SunshineCard.Content>
              </SunshineCard>
              {mandate.label ? (
                <>
                  <SunshineCard>
                    <SunshineCard.Content
                      flexDirection="row"
                      justifyContent="space-between"
                    >
                      <Text>{locales.recapItems.label}</Text>
                      <Text fontWeight="weight-500" variant="primary">
                        {mandate.label}
                      </Text>
                    </SunshineCard.Content>
                  </SunshineCard>
                </>
              ) : null}
              <SunshineCard>
                <SunshineCard.Content
                  flexDirection="row"
                  justifyContent="space-between"
                >
                  <Text>{locales.recapItems.uniqueMandateReference}</Text>
                  <Text fontWeight="weight-500" variant="primary">
                    {mandate.uniqueMandateReference}
                  </Text>
                </SunshineCard.Content>
              </SunshineCard>
              <SunshineCard>
                <SunshineCard.Content
                  flexDirection="row"
                  justifyContent="space-between"
                >
                  <Text>{locales.recapItems.creditorIdentifier}</Text>
                  <Text fontWeight="weight-500" variant="primary">
                    {mandate.creditorIdentifier}
                  </Text>
                </SunshineCard.Content>
              </SunshineCard>
            </SunshineCard.Group>

            <SunshineCard
              isChecked={hasApprovedAgreement}
              onChange={(event) =>
                setHasApprovedAgreement(event.target.checked)
              }
              title={locales.agreementCheckbox.title}
              variant="checkbox"
            >
              <SunshineCard.Content>
                <Text>
                  {hasMultipleBankAccounts
                    ? locales.formatString(
                        locales.agreementCheckbox
                          .descriptionWithBankAccountName,
                        { bankAccountName },
                      )
                    : locales.agreementCheckbox.description}
                </Text>
              </SunshineCard.Content>
            </SunshineCard>
          </StepperLayout.BodyScrollableSection>

          {showPasscodeSidebar ? null : (
            <CommonStyles.FooterContainer>
              <NextButton isDisabled={!canClickOnNext} type="submit">
                {locales.ctaAllGood}
              </NextButton>
            </CommonStyles.FooterContainer>
          )}
        </CommonStyles.FormWrapper>
      </StepperLayout.Body>

      {showPasscodeSidebar ? (
        <StepperLayout.Sidebar>
          <SidebarConfirmationPasscode
            onConfirmationSuccess={onConfirmationSuccess}
          />
        </StepperLayout.Sidebar>
      ) : null}
    </StepperLayout.Root>
  );
};

export default StepMandateRecap;
